import { AnchorHTMLAttributes, PropsWithChildren } from 'react'
import classNames from 'classnames'
import NextLink, { LinkProps as NextLinkProps } from 'next/link'

import Typography from '@/components/base/Typography'

type HTMLAnchorProps = AnchorHTMLAttributes<HTMLAnchorElement>
type NextLinkPropsWithChildren = PropsWithChildren<NextLinkProps>

type LinkProps = (HTMLAnchorProps | NextLinkPropsWithChildren) &
  Partial<{
    external: boolean
    className: string
    textClassName: string
  }>

const Link = ({
  external,
  className,
  textClassName,
  children,
  ...props
}: LinkProps) => {
  const variantClasses = classNames(
    'group inline-flex items-end p-xxs -m-xxs',
    'before:mr-[1rem] before:block before:h-xxs before:w-xxs before:shrink-0 before:rounded-full before:bg-current'
  )

  const renderLink = () => {
    return (
      <span
        data-label={children as string}
        className={classNames(
          'relative -my-[.45rem] flex overflow-hidden py-[.45rem]',
          'after:absolute after:left-0 after:top-0 after:-my-[.45em] after:w-full after:translate-y-full after:py-[.45rem] after:font-roboto after:text-button after:transition-transform after:duration-300 after:ease-out-cubic after:content-[attr(data-label)] pointer:group-hover:after:translate-y-0',
          textClassName
        )}
      >
        <Typography
          variant="button"
          className={classNames(
            'block transition-transform duration-300 ease-out-cubic pointer:group-hover:-translate-y-full',
            textClassName
          )}
        >
          {children}
        </Typography>
      </span>
    )
  }

  return external ? (
    <a
      target="_blank"
      className={classNames(variantClasses, className)}
      {...(props as HTMLAnchorProps)}
    >
      {renderLink()}
    </a>
  ) : (
    <NextLink
      scroll={false}
      className={classNames(variantClasses, className)}
      {...(props as NextLinkPropsWithChildren)}
    >
      {renderLink()}
    </NextLink>
  )
}

export default Link
